import { isDefined } from 'common/TypeUtilities';
import { AnchorLinks } from 'common/urls/AnchorLinks';
import { first } from 'lodash';
import { useCallback } from 'react';
import { BrowseFilter, CategoryPageType, CuisineRegion, ReferralStripTextColor } from 'src/gqlReactTypings.generated.d';
import { useGetCuisineCategoryById } from 'src/pages/consumer/dish-first/explore/components/CategoryPages/useIsSubcuisineOf';
import { CategoryPageRecentReviews } from 'src/pages/consumer/dish-first/explore/components/Segments/CategoryPageRecentReviews';
import { CategoryPageReferralStrip } from 'src/pages/consumer/dish-first/explore/components/Segments/CategoryPageReferralStrip/CategoryPageReferralStrip';
import { useCurrentUser } from 'src/shared/hooks/useCurrentUserHook';
import { match, P } from 'ts-pattern';
import { DietaryTags } from 'web-common/src/shared/Enums';
import { TopBannerRow } from '../../components/Banners/TopBannerRow';
import { BrowseAllShefsSection } from '../../components/BrowseAllShefsSection';

import { AllPreferencesFilters } from '../../components/BrowseBy/BrowseByTags/AllPreferencesFilters';
import { CategoryTop } from '../../components/BrowseBy/BrowseByTags/Categories/CategoryTop';
import getCategories, { getCategoryLabel } from '../../components/BrowseBy/BrowseByTags/Categories/getCategories';
import { Collections } from '../../components/BrowseBy/BrowseByTags/Collections';
import { SubcuisinePickerCarousel } from '../../components/SubcuisinePickerCarousel';
import { useAllCuisinesShowing } from '../../store';

import { getRootCuisineCategories } from '../../components/ExploreFilterModals/CuisineFilterModal/utils';
import { UseAdditionalContentProps } from './types';
import { compactWithUpdatedIndices } from './utils';

export const useAdditionalContent = (props: UseAdditionalContentProps) => {
  const {
    allCuisineCategories,
    availableCuisineCategories,
    filters,
    foodPreferenceSelected = false,
    handleFiltersChange,
    isFiltersLoading = false,
    isOnSeeAllPage = false,
    seeAllCuisinesIndex = 99,
    region,
  } = props;
  const { updateQueryParams } = filters;
  const [currentUser] = useCurrentUser();
  const { isAllCuisinesShowing, showAllCuisines } = useAllCuisinesShowing();

  const subcuisinePicker = (
    <SubcuisinePickerCarousel
      key={'subcuisine-carousel'}
      loading={isFiltersLoading}
      allCuisineCategories={allCuisineCategories}
      availableCuisineCategories={availableCuisineCategories}
      onFiltersChange={handleFiltersChange}
      filters={filters}
      showFiltersInline={true}
    />
  );

  const handleBrowseAllShefsButtonClick = useCallback(() => {
    showAllCuisines();
  }, [showAllCuisines]);

  const cuisineCategory = useGetCuisineCategoryById(first(filters.cuisineFilters));
  const isCategoryPage = !!cuisineCategory?.categoryPageContent?.pageType;
  const isTopLevelCategoryPage = cuisineCategory?.categoryPageContent?.pageType === CategoryPageType.TopLevel;

  // We only show the Top Cuisines category if there are a sufficient number of cuisines (8). Otherwise
  // sparse shef lite markets it looks kind of weird.
  const availableCuisines = getRootCuisineCategories(availableCuisineCategories).map((c) => getCategoryLabel(c.title));
  const topCategoriesForRegion = region ? getCategories(region.id) : [];
  const topCategoriesForRegionThatAreAvailable = topCategoriesForRegion.filter((c) =>
    availableCuisines.includes(getCategoryLabel(c.value))
  );
  const showTopCuisineCategories = availableCuisines.length > topCategoriesForRegionThatAreAvailable.length * 2;

  const possibleAdditionalContent = match({
    isTopLevelCategoryPage,
    isCategoryPage,
    cuisineCategoryId: cuisineCategory?.id,
    dietaryFilter: first(filters.dietaryFilters),
    browseFilter: filters.browseFilter,
    isOnSeeAllPage,
    foodPreferenceSelected,
    seeAllCuisines: isAllCuisinesShowing,

    isVerifiedHalalEnabled: region?.isVerifiedHalalEnabled,
  })
    .with({ dietaryFilter: DietaryTags.HALAL, isVerifiedHalalEnabled: true, isOnSeeAllPage: false }, () => [
      {
        node: (
          <CategoryPageReferralStrip
            backgroundConfig={{
              referralStripTextColor: ReferralStripTextColor.Light,
              referralStripBackgroundUrl:
                'https://shef-general.s3.us-west-1.amazonaws.com/assets/img/category-pages/halal-dietary/referral-background/referral-background-mobile.jpg',
              referralStripBackgroundUrlTablet:
                'https://shef-general.s3.us-west-1.amazonaws.com/assets/img/category-pages/halal-dietary/referral-background/referral-background-tablet.jpg',
              referralStripBackgroundUrlDesktop:
                'https://shef-general.s3.us-west-1.amazonaws.com/assets/img/category-pages/halal-dietary/referral-background/referral-background-desktop.jpg',
            }}
          />
        ),
        index: 98,
      },
      { node: subcuisinePicker, index: 99 }, // Always last
    ])
    .with(
      {
        isTopLevelCategoryPage: true,
        browseFilter: BrowseFilter.Cuisine,
        isOnSeeAllPage: false,
        cuisineCategoryId: CuisineRegion.Indian,
      },
      () => [
        {
          node: <CategoryPageRecentReviews />,
          index: 2,
        },
        {
          node: <CategoryPageReferralStrip backgroundConfig={cuisineCategory?.categoryPageContent} />,
          index: 98,
        },
      ]
    )
    .with({ isTopLevelCategoryPage: true, browseFilter: BrowseFilter.Cuisine, isOnSeeAllPage: false }, () => [
      {
        node: <CategoryPageReferralStrip backgroundConfig={cuisineCategory?.categoryPageContent} />,
        index: 98,
      },
    ])
    .with({ isCategoryPage: true, browseFilter: BrowseFilter.Cuisine, isOnSeeAllPage: false }, () => [
      {
        node: <CategoryPageReferralStrip backgroundConfig={cuisineCategory?.categoryPageContent} />,
        index: 98,
      },
    ])
    .with({ isOnSeeAllPage: true }, () => [{ node: subcuisinePicker, index: 6 }])
    .with({ cuisineCategoryId: P.not(P.nullish) }, () => [{ node: subcuisinePicker, index: 6 }])
    .with({ seeAllCuisines: false }, () => [
      { node: <TopBannerRow regionId={region?.id ?? 0} />, index: 1 },
      showTopCuisineCategories
        ? {
            node: (
              <CategoryTop
                updateQueryParams={updateQueryParams}
                cuisineCategories={topCategoriesForRegionThatAreAvailable}
              />
            ),
            index: (currentUser?.orderCount ?? 0) === 0 ? 2 : 4,
          }
        : undefined,
      {
        node: <div id={`${AnchorLinks.EXPLORE_SEE_ALL_CUISINES}`} />,
        index: 98, // always last
      },
      {
        node: <BrowseAllShefsSection onClick={handleBrowseAllShefsButtonClick} />,
        index: 99, // always last
      },
    ])
    .with({ seeAllCuisines: true }, () => [
      { node: <TopBannerRow regionId={region?.id ?? 0} />, index: 1 },
      showTopCuisineCategories
        ? {
            node: (
              <CategoryTop
                updateQueryParams={updateQueryParams}
                cuisineCategories={topCategoriesForRegionThatAreAvailable}
              />
            ),
            index: (currentUser?.orderCount ?? 0) === 0 ? 2 : 4,
          }
        : undefined,
      {
        node: <div id={`${AnchorLinks.EXPLORE_SEE_ALL_CUISINES}`} />,
        index: seeAllCuisinesIndex,
      },
    ])
    .otherwise(() => [
      { node: <TopBannerRow regionId={region?.id ?? 0} />, index: 0 },
      {
        node: <Collections updateQueryParams={updateQueryParams} />,
        index: 5,
      },
      { node: subcuisinePicker, index: 6 },
      {
        node: <AllPreferencesFilters filters={filters} updateQueryParams={updateQueryParams}></AllPreferencesFilters>,
        index: 11,
      },
    ]);

  return {
    additionalContent: compactWithUpdatedIndices(possibleAdditionalContent.filter(isDefined)),
  };
};
